import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'
import { MdDeleteForever } from 'react-icons/md'
import { IoIosAttach } from 'react-icons/io'

import Progress from './progress'
import UploadFileImage from './upload-file'
import Button from '../Button'
import Modal from '../Modal'
import { sanitizeFileName, validateFiles } from '../../utils/file-validation'

const ACCEPT_MAP = {
  image: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
  pdf: ['application/pdf'],
  text: ['text/plain'],
  doc: [
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ],
  excel: [
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ],
} as const

type FileCategory = keyof typeof ACCEPT_MAP

type ProgressMap = Record<string, number>

type SetFieldValue = (
  field: string,
  value: unknown,
  shouldValidate?: boolean,
) => void

interface FileUploaderProps {
  setFieldValue: SetFieldValue
  children: React.ReactNode
  files: File[]
  setFiles: React.Dispatch<React.SetStateAction<File[]>>
  multiple?: boolean
  allowedTypes?: FileCategory[]
  fieldName?: string
}

interface FileUploaderContextProps {
  files: File[]
  progress: ProgressMap
  handleFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  handleDeleteFile: () => void
  openDeleteConfirmation: (fileName: string) => void
  openUploadModal: () => void
}

const FileUploaderContext = createContext<FileUploaderContextProps | null>(null)

function buildAcceptString(allowedTypes: readonly FileCategory[]): string {
  const mimeTypes = allowedTypes.reduce<string[]>((acc, type) => {
    return acc.concat(ACCEPT_MAP[type])
  }, [])
  return mimeTypes.join(',')
}

function sanitizeFiles(files: File[]): File[] {
  return files.map((file) => {
    const sanitizedName = sanitizeFileName(file.name)
    if (sanitizedName !== file.name) {
      if (typeof File !== 'undefined') {
        try {
          return new File([file], sanitizedName, {
            type: file.type,
            lastModified: file.lastModified,
          })
        } catch {
          return file
        }
      }
      return file
    }
    return file
  })
}

export function FileUploader({
  setFieldValue,
  children,
  files,
  setFiles,
  multiple = true,
  allowedTypes = ['image', 'pdf'],
  fieldName = 'anexarArquivo',
}: FileUploaderProps) {
  const [progress, setProgress] = useState<ProgressMap>({})
  const [isOpenDeleteFileConfirmation, setIsOpenDeleteFileConfirmation] =
    useState(false)
  const [isOpenUploadModal, setIsOpenUploadModal] = useState(false)
  const [fileToDelete, setFileToDelete] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)

  const accept = useMemo(() => buildAcceptString(allowedTypes), [allowedTypes])

  const startFakeProgress = useCallback((fileName: string) => {
    let uploadProgress = 0

    setProgress((prev) => ({ ...prev, [fileName]: uploadProgress }))

    const interval = window.setInterval(() => {
      uploadProgress += 20

      setProgress((prev) => ({ ...prev, [fileName]: uploadProgress }))

      if (uploadProgress >= 100) {
        window.clearInterval(interval)
      }
    }, 200)
  }, [])

  const handleFiles = useCallback(
    async (newFiles: File[]) => {
      try {
        const { validFiles, invalidFiles } = await validateFiles(newFiles)

        if (invalidFiles.length > 0) {
          const errorMessages = invalidFiles.map(
            ({ file, errors }: { file: File; errors: string[] }) =>
              `${file.name}: ${errors.join(', ')}`,
          )
          alert(`Arquivos inválidos:\n${errorMessages.join('\n')}`)
        }

        if (validFiles.length === 0) return

        const sanitizedFiles = sanitizeFiles(validFiles)

        setFiles((prev) => {
          const updated = [...prev, ...sanitizedFiles]
          setFieldValue(fieldName, updated)
          return updated
        })

        sanitizedFiles.forEach((file) => startFakeProgress(file.name))

        setIsOpenUploadModal(false)
      } catch (error) {
        console.error('Erro na validação dos arquivos:', error)
        alert('Erro ao processar os arquivos. Tente novamente.')
      }
    },
    [fieldName, setFieldValue, setFiles, startFakeProgress],
  )

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFiles = event.target.files
      if (!selectedFiles) return

      void handleFiles(Array.from(selectedFiles))

      event.target.value = ''
    },
    [handleFiles],
  )

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setIsDragging(false)

      const droppedFiles = Array.from(e.dataTransfer.files)
      void handleFiles(droppedFiles)
    },
    [handleFiles],
  )

  const openDeleteConfirmation = useCallback((fileName: string) => {
    setFileToDelete(fileName)
    setIsOpenDeleteFileConfirmation(true)
  }, [])

  const handleDeleteFile = useCallback(() => {
    if (!fileToDelete) return

    setFiles((prev) => {
      const updated = prev.filter((file) => file.name !== fileToDelete)
      setFieldValue(fieldName, updated)
      return updated
    })

    setProgress((prev) => {
      const updated = { ...prev }
      delete updated[fileToDelete]
      return updated
    })

    setIsOpenDeleteFileConfirmation(false)
    setFileToDelete(null)
  }, [fileToDelete, fieldName, setFieldValue, setFiles])

  const openUploadModal = useCallback(() => {
    setIsOpenUploadModal(true)
  }, [])

  return (
    <FileUploaderContext.Provider
      value={{
        files,
        progress,
        handleFileChange,
        handleDeleteFile,
        openDeleteConfirmation,
        openUploadModal,
      }}
    >
      {children}

      {isOpenDeleteFileConfirmation && (
        <Modal
          isOpen={isOpenDeleteFileConfirmation}
          onRequestClose={() => setIsOpenDeleteFileConfirmation(false)}
        >
          <p>Essa ação não pode ser revertida.</p>
          <div className="w-full h-px bg-tertiary-5" />
          <div className="flex flex-col desktop:flex-row gap-3">
            <Button onClick={() => setIsOpenDeleteFileConfirmation(false)}>
              Cancelar
            </Button>
            <Button onClick={handleDeleteFile}>Apagar</Button>
          </div>
        </Modal>
      )}

      {isOpenUploadModal && (
        <Modal
          isOpen={isOpenUploadModal}
          onRequestClose={() => setIsOpenUploadModal(false)}
        >
          <div
            className={`rounded-lg text-center transition-colors p-9 ${isDragging ? 'bg-primary-5' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <UploadFileImage className="fill-[rgb(var(--primary-1))] mx-auto" />
            <p className="my-1">Arraste um arquivo para cá</p>
            <p className="mb-1">ou</p>

            <Button className="w-full desktop:w-fit mx-auto" type="button">
              <label htmlFor="modalFileInput" className="cursor-pointer">
                Selecionar arquivo do computador
              </label>
              <input
                id="modalFileInput"
                type="file"
                className="hidden"
                onChange={handleFileChange}
                accept={accept}
                multiple={multiple}
              />
            </Button>
          </div>
        </Modal>
      )}
    </FileUploaderContext.Provider>
  )
}

type FileListProps = {
  classNames?: string
  showProgress?: boolean
  progressDirection?: 'row' | 'column'
  showName?: boolean
  showIcon?: boolean
  isDeletable?: boolean
}

FileUploader.List = function FileList({
  classNames,
  showProgress = true,
  progressDirection = 'row',
  showName = true,
  showIcon = true,
  isDeletable = true,
}: FileListProps) {
  const context = useContext(FileUploaderContext)
  if (!context) return null

  const { files, progress, openDeleteConfirmation } = context

  return (
    <div className={`w-full ${classNames ?? ''}`}>
      {files.map((file) => (
        <div key={file.name} className="flex w-full flex-col">
          <div className="flex items-center gap-2">
            {showIcon && <IoIosAttach size={20} />}
            <div
              className={`flex ${
                progressDirection === 'column' ? 'flex-col' : 'flex-row'
              } items-center gap-1 ${showProgress ? 'w-full' : 'w-fit'}`}
            >
              {showName && (
                <p className="truncate overflow-hidden" title={file.name}>
                  {file.name.length > 24
                    ? `${file.name.substring(0, 24)}...`
                    : file.name}
                </p>
              )}

              {showProgress && (
                <Progress
                  progress={progress[file.name] ?? 0}
                  height="h-1"
                  width="w-full"
                />
              )}
            </div>

            {isDeletable && (
              <MdDeleteForever
                size={20}
                onClick={() => openDeleteConfirmation(file.name)}
                className="cursor-pointer hover:text-primary-1"
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ')
                    openDeleteConfirmation(file.name)
                }}
              />
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

type FileButtonProps = React.ComponentProps<typeof Button> & {
  children?: React.ReactNode
}

FileUploader.Button = function FileButton({
  children,
  ...rest
}: FileButtonProps) {
  const context = useContext(FileUploaderContext)
  if (!context) return null

  const { openUploadModal } = context

  return (
    <Button type="button" onClick={openUploadModal} {...rest}>
      {children}
    </Button>
  )
}
